import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import {
  BookOpen,
  Loader2,
  Sparkles,
  TrendingUp,
  Target,
  AlertCircle,
  Lightbulb,
  Copy,
  Check,
  ChevronRight,
  Calendar
} from 'lucide-react';

interface Reflection {
  id: string;
  lesson_id: string;
  mode: string;
  content: string;
  created_at: string;
  lesson_title: string;
  course_title: string;
}

interface Insight {
  type: 'growth' | 'pattern' | 'blindspot' | 'strength';
  text: string;
}

interface Recommendation {
  topic: string;
  reason: string;
  lessonId?: string;
  lessonTitle?: string;
}

interface Analysis {
  reflectionCount: number;
  dateRange: {
    first: string;
    last: string;
  };
  insights: Insight[];
  recommendations: Recommendation[];
}

interface AnalysisResponse {
  analysis: Analysis;
  generatedAt: string;
}

const insightIcons: Record<string, typeof TrendingUp> = {
  growth: TrendingUp,
  pattern: Target,
  blindspot: AlertCircle,
  strength: Sparkles
};

const insightColors: Record<string, string> = {
  growth: 'text-green-400 bg-green-500/10 border-green-500/20',
  pattern: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
  blindspot: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
  strength: 'text-purple-400 bg-purple-500/10 border-purple-500/20'
};

const insightLabels: Record<string, string> = {
  growth: 'Growth',
  pattern: 'Pattern',
  blindspot: 'Blind Spot',
  strength: 'Strength'
};

export default function Journal() {
  const { user } = useAuth();
  const { fetchApi } = useApi();
  const [reflections, setReflections] = useState<Reflection[]>([]);
  const [analysis, setAnalysis] = useState<AnalysisResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (user) {
      loadReflections();
    } else {
      setLoading(false);
    }
  }, [user]);

  async function loadReflections() {
    try {
      const data = await fetchApi<{ reflections: Reflection[] }>('/api/reflections?limit=100');
      setReflections(data.reflections || []);
    } catch (err) {
      console.error('Failed to load reflections:', err);
      setError('Failed to load your reflections');
    } finally {
      setLoading(false);
    }
  }

  async function handleAnalyze() {
    setAnalyzing(true);
    setError(null);

    try {
      const result = await fetchApi<AnalysisResponse>('/api/reflections/analyze', {
        method: 'POST'
      });
      setAnalysis(result);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Analysis failed';
      if (errorMessage.includes('at least 3')) {
        setError('You need at least 3 reflections before we can analyze your growth patterns.');
      } else {
        setError('Analysis failed. Please try again later.');
      }
      console.error('Analysis error:', err);
    } finally {
      setAnalyzing(false);
    }
  }

  function generateSummaryText(): string {
    if (!analysis) return '';

    const { analysis: a, generatedAt } = analysis;
    const date = new Date(generatedAt).toLocaleDateString();

    let text = `My Thinking Journal - Growth Summary\n`;
    text += `Generated: ${date}\n\n`;
    text += `${a.reflectionCount} reflections from ${a.dateRange.first} to ${a.dateRange.last}\n\n`;
    text += `Key Insights:\n`;
    a.insights.forEach(insight => {
      text += `- [${insightLabels[insight.type]}] ${insight.text}\n`;
    });
    text += `\nRecommended Next Steps:\n`;
    a.recommendations.forEach(rec => {
      text += `- ${rec.topic}: ${rec.reason}\n`;
    });
    text += `\n— Generated by Northcoast AI Academy`;

    return text;
  }

  async function handleCopy() {
    const text = generateSummaryText();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  }

  if (!user) {
    return (
      <div className="max-w-4xl mx-auto text-center py-12">
        <BookOpen className="w-16 h-16 text-gray-600 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-white mb-2">Thinking Journal</h1>
        <p className="text-gray-400 mb-6">
          Sign in to view your reflection journal and growth analysis.
        </p>
        <Link
          to="/login"
          className="inline-flex items-center gap-2 px-6 py-3 bg-accent-blue hover:bg-accent-blue/80 text-slate-900 rounded-lg font-medium transition-colors"
        >
          Sign In
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
          <BookOpen className="w-8 h-8 text-accent-blue" />
          Thinking Journal
        </h1>
        <p className="text-gray-400">
          Your reflections and growth over time
        </p>
      </div>

      {/* Stats Bar */}
      <div className="bg-gray-800/50 rounded-xl p-4 mb-6 flex flex-wrap items-center gap-6">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-gray-500" />
          <span className="text-white font-medium">{reflections.length}</span>
          <span className="text-gray-400">reflections</span>
        </div>
        {reflections.length > 0 && (
          <>
            <div className="text-gray-600">|</div>
            <div className="text-gray-400 text-sm">
              First: {new Date(reflections[reflections.length - 1]?.created_at).toLocaleDateString()}
            </div>
            <div className="text-gray-400 text-sm">
              Latest: {new Date(reflections[0]?.created_at).toLocaleDateString()}
            </div>
          </>
        )}
      </div>

      {/* Analysis Section */}
      <div className="mb-8">
        {!analysis ? (
          <div className="bg-gradient-to-br from-purple-900/30 to-blue-900/30 border border-purple-500/20 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-6 h-6 text-purple-400" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-white mb-2">
                  Analyze Your Growth
                </h2>
                <p className="text-gray-300 mb-4">
                  Our AI will analyze your reflections to identify patterns, growth areas,
                  and personalized recommendations for your learning journey.
                </p>

                {error && (
                  <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}

                <button
                  onClick={handleAnalyze}
                  disabled={analyzing || reflections.length < 3}
                  className="px-6 py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors flex items-center gap-2"
                >
                  {analyzing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Analyzing your reflections...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Analyze My Growth
                    </>
                  )}
                </button>

                {reflections.length < 3 && (
                  <p className="text-gray-500 text-sm mt-3">
                    Complete at least 3 reflections to unlock analysis ({reflections.length}/3)
                  </p>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-gradient-to-br from-purple-900/30 to-blue-900/30 border border-purple-500/20 rounded-xl overflow-hidden">
            {/* Analysis Header */}
            <div className="p-6 border-b border-purple-500/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Sparkles className="w-6 h-6 text-purple-400" />
                  <h2 className="text-xl font-semibold text-white">Your Growth Analysis</h2>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleCopy}
                    className="px-3 py-1.5 text-sm text-gray-400 hover:text-white hover:bg-gray-700/50 rounded-lg transition-colors flex items-center gap-1.5"
                  >
                    {copied ? (
                      <>
                        <Check className="w-4 h-4 text-green-400" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        Copy Summary
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleAnalyze}
                    disabled={analyzing}
                    className="px-3 py-1.5 text-sm text-purple-400 hover:text-purple-300 hover:bg-purple-500/10 rounded-lg transition-colors"
                  >
                    {analyzing ? 'Refreshing...' : 'Refresh'}
                  </button>
                </div>
              </div>
              <p className="text-gray-400 text-sm mt-1">
                Based on {analysis.analysis.reflectionCount} reflections from{' '}
                {analysis.analysis.dateRange.first} to {analysis.analysis.dateRange.last}
              </p>
            </div>

            {/* Insights */}
            <div className="p-6 border-b border-purple-500/20">
              <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-amber-400" />
                Key Insights
              </h3>
              <div className="space-y-3">
                {analysis.analysis.insights.map((insight, index) => {
                  const Icon = insightIcons[insight.type] || Lightbulb;
                  const colorClass = insightColors[insight.type] || insightColors.pattern;

                  return (
                    <div
                      key={index}
                      className={`p-4 rounded-lg border ${colorClass}`}
                    >
                      <div className="flex items-start gap-3">
                        <Icon className="w-5 h-5 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="text-xs font-medium uppercase tracking-wide opacity-75">
                            {insightLabels[insight.type]}
                          </span>
                          <p className="text-white mt-1">{insight.text}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Recommendations */}
            <div className="p-6">
              <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-blue-400" />
                Recommended Next Steps
              </h3>
              <div className="space-y-3">
                {analysis.analysis.recommendations.map((rec, index) => (
                  <div
                    key={index}
                    className="p-4 bg-gray-800/50 rounded-lg border border-gray-700 hover:border-gray-600 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h4 className="text-white font-medium">{rec.topic}</h4>
                        <p className="text-gray-400 text-sm mt-1">{rec.reason}</p>
                      </div>
                      {rec.lessonId && (
                        <Link
                          to={`/lessons/${rec.lessonId}`}
                          className="flex-shrink-0 px-3 py-1.5 text-sm text-accent-blue hover:text-white hover:bg-accent-blue/20 rounded-lg transition-colors flex items-center gap-1"
                        >
                          Start
                          <ChevronRight className="w-4 h-4" />
                        </Link>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Reflections List */}
      <div>
        <h2 className="text-xl font-semibold text-white mb-4">Your Reflections</h2>

        {reflections.length === 0 ? (
          <div className="bg-gray-800/50 rounded-xl p-8 text-center">
            <BookOpen className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No reflections yet</h3>
            <p className="text-gray-400 mb-4">
              Complete lessons and write reflections to track your thinking growth.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-4 py-2 bg-accent-blue hover:bg-accent-blue/80 text-slate-900 rounded-lg transition-colors"
            >
              <BookOpen className="w-4 h-4" />
              Browse Tracks
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {reflections.map(reflection => (
              <div
                key={reflection.id}
                className="bg-gray-800/50 rounded-xl p-5 border border-gray-700"
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <h3 className="text-white font-medium">{reflection.lesson_title}</h3>
                    <p className="text-gray-500 text-sm">{reflection.course_title}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-gray-500 text-sm">
                      {new Date(reflection.created_at).toLocaleDateString()}
                    </span>
                    <span className={`block text-xs mt-1 px-2 py-0.5 rounded-full ${
                      reflection.mode === 'NO_TOOL'
                        ? 'bg-green-500/20 text-green-400'
                        : reflection.mode === 'AUDIT'
                          ? 'bg-amber-500/20 text-amber-400'
                          : 'bg-blue-500/20 text-blue-400'
                    }`}>
                      {reflection.mode.replace('_', ' ')}
                    </span>
                  </div>
                </div>
                <p className="text-gray-300 whitespace-pre-wrap">{reflection.content}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
